'use client';

import { useEffect, useRef } from 'react';
import { toast } from '@cobrastyle/ui';
import { useCart } from '@/hooks';
import { customerMessage, translateBackendMessage } from '@/lib/backend-messages';

/**
 * Cart mutations in the engine run inside `startTransition`, so the promise
 * returned by `addToCart`/`removeItem`/`applyCoupon` resolves immediately and
 * never rejects — the outcome only shows up as an edge in the shared
 * `{ isLoading, error }` state. This hook turns that edge into a toast.
 *
 * Call `notify(...)` synchronously right before invoking a cart mutation. When
 * the mutation settles (loading true → false) exactly one toast fires: the
 * error message if the engine reported one, otherwise the success message.
 *
 * Multiple instances can coexist on one page (e.g. remove-item and coupon):
 * only the instance that called `notify` for the in-flight action has a pending
 * entry, so only it toasts.
 */
interface CartFeedback {
  /** Toast shown on success. Omit to run no success toast (e.g. add-to-cart,
   *  where the drawer opening is the confirmation). */
  success?: string;
  /** Toast shown on failure. */
  error?: string;
  /** Ran on the success edge, after the toast (if any). */
  onSuccess?: () => void;
  /** Ran on the failure edge, after the toast (if any). */
  onError?: () => void;
}

export function useCartFeedback() {
  const { isLoading, error } = useCart();
  const pending = useRef<CartFeedback | null>(null);
  const wasLoading = useRef(false);

  useEffect(() => {
    // Completion edge: a mutation we initiated has finished.
    if (wasLoading.current && !isLoading && pending.current) {
      const feedback = pending.current;
      pending.current = null;
      if (error) {
        // Prefer the backend's specific reason (e.g. "Not enough items for
        // sale") in Swedish over the generic armed message. A reason with no
        // Swedish wording gives way to the armed message, never shown in English.
        const message = feedback.error
          ? customerMessage(error, feedback.error)
          : translateBackendMessage(error);
        if (message) toast.error(message);
        feedback.onError?.();
      } else {
        if (feedback.success) toast.success(feedback.success);
        feedback.onSuccess?.();
      }
    }
    wasLoading.current = isLoading;
  }, [isLoading, error]);

  return {
    /** Arm feedback for the next cart mutation. Call immediately before it. */
    notify(feedback: CartFeedback) {
      pending.current = feedback;
    },
  };
}
